import { FormikProvider, useFormik } from 'formik'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'

import { dollarsToCents, formatCurrency } from '@/utils/currencyHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  getProjectedW2Income,
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'

import { Alert, Form, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'
import { W2IncomeWarning } from './W2IncomeWarning'
import {
  PaystubVsEstimateAccordion,
  PaystubAccordionContent,
} from './PaystubVsEstimateAccordion'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { ExamplePaystub } from './ExamplePaystub'

// TODO: Migrate to V2
import {
  FormikLabelError,
  FormikRadioToggleButton,
  getFieldName,
  makeNumberSchema,
  makeReqBoolSchema,
} from '@/base'
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'

const scorpW2IncomeCopy = (
  activeQuarterDetails?: QuarterlyTaxEstimateDetail
) => {
  switch (activeQuarterDetails?.taxQuarter) {
    case '1':
      return "Enter income earned through your private practice for pay periods starting January 1st through February 28th. You'll find this on the first paycheck you paid yourself in March."
    case '2':
      return "Enter income earned through your private practice for pay periods starting January 1st through April 30th. You'll find this on the first paycheck you paid yourself in May."
    case '3':
      return "Enter income earned through your private practice for pay periods starting January 1st through July 31st. You'll find this on the first paycheck you paid yourself in August."
    case '4':
      return "Enter income earned through your private practice for pay periods starting January 1st through November 30st. You'll find this on the first paycheck you paid yourself in December."
    // should never happen, but fall back to generic copy
    default:
      return 'Please enter income from your most recent paystub. Do not enter your net profit.'
  }
}

export const ScorpW2PrivatePracticeIncome = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.scorpW2Earnings
  )

  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const scorpW2IncomeSubcopy = scorpW2IncomeCopy(activeQuarterDetails)

  const someIncomeEntered =
    formFlowAnswers.scorpPracticeWIncome !== null ||
    formFlowAnswers.estimatedAnnualScorpPracticeW2Income !== null

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      enterScorpPaystubW2: someIncomeEntered
        ? formFlowAnswers.scorpPracticeWIncome !== null
        : undefined,
      scorpPracticeWIncome: formFlowAnswers.scorpPracticeWIncome,
      estimatedAnnualScorpPracticeW2Income:
        formFlowAnswers.estimatedAnnualScorpPracticeW2Income,
    },
    onSubmit: async ({
      enterScorpPaystubW2,
      scorpPracticeWIncome,
      estimatedAnnualScorpPracticeW2Income,
    }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          scorpPracticeWIncomeInCents: enterScorpPaystubW2
            ? dollarsToCents(scorpPracticeWIncome)
            : null,
          estimatedAnnualScorpPracticeW2IncomeInCents: enterScorpPaystubW2
            ? null
            : dollarsToCents(estimatedAnnualScorpPracticeW2Income),
        })
      )

      if (res) {
        const newAnswers = {
          ...formFlowAnswers,
          scorpPracticeWIncome,
          estimatedAnnualScorpPracticeW2Income,
        }

        if (enterScorpPaystubW2) {
          newAnswers.estimatedAnnualScorpPracticeW2Income = null
        } else {
          newAnswers.scorpPracticeWIncome = null
        }

        setFormFlowAnswers(newAnswers)
        track('scorp all w2 income submitted', {
          scorp_practice_w_income_in_cents: enterScorpPaystubW2
            ? dollarsToCents(scorpPracticeWIncome)
            : null,
          enter_scorp_paystub_w2: Boolean(enterScorpPaystubW2),
          estimated_annual_scorp_practice_w_income: enterScorpPaystubW2
            ? null
            : dollarsToCents(estimatedAnnualScorpPracticeW2Income),
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { values, isSubmitting, isValid, submitForm, touched } = formik

  const projectedScorpW2Income = getProjectedW2Income({
    ytdIncomeInDollars: values.scorpPracticeWIncome,
    quarterDetails: activeQuarterDetails,
  })

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <ExamplePaystub zoomTo="withholdings" />

        <Text
          variant={TypographyStyles.DISPLAY_SM}
          className="max-w-3xl mx-auto text-center"
        >
          Your S corporation W-2 earnings
        </Text>

        <Text
          variant={TypographyStyles.BODY_LG}
          className="max-w-3xl mx-auto text-center"
        >
          We need to know your income for the year to calculate your tax
          bracket. You can tell us how much you’ve made so far by entering the
          year-to-date (YTD) amount from your paystub, or an estimate of how
          much you will make for the entire year.
        </Text>

        {error && (
          <Alert
            variant="error"
            className="max-w-3xl mx-auto"
            description={error.message}
          />
        )}

        <div className="max-w-2xl mx-auto space-y-4">
          <FormikLabelError
            name={getFieldName<typeof values>('enterScorpPaystubW2')}
            label="How would you like to enter income?"
            description="Click on “How should I choose?” below if you need help deciding how to enter income."
            schema={makeReqBoolSchema()}
          />

          <div className="flex flex-col sm:flex-row gap-4">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('enterScorpPaystubW2')}
              value
              fullWidth
            >
              Year-to-date from paystub
            </FormikRadioToggleButton>
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('enterScorpPaystubW2')}
              value={false}
              fullWidth
            >
              Estimated income
            </FormikRadioToggleButton>
          </div>
        </div>

        {values.enterScorpPaystubW2 && (
          <div className="max-w-2xl mx-auto space-y-4">
            <LabelDescription
              required
              label="Year-to-Date W-2 gross income earned as an employee of your S corp"
              description={scorpW2IncomeSubcopy}
            />

            <div className="flex flex-col lg:flex-row gap-4 items-center">
              <div className="lg:w-1/2 w-full">
                <Form.Input
                  name={getFieldName<typeof values>('scorpPracticeWIncome')}
                  required
                  componentType="currency"
                  placeholder="$"
                  schema={makeNumberSchema({
                    allowedDecimals: 2,
                    greaterThanZero: true,
                  })}
                />
              </div>
              <div className="lg:w-1/2 w-full">
                <Alert
                  title={
                    projectedScorpW2Income
                      ? formatCurrency(projectedScorpW2Income, {
                          hideDecimalsIfZero: true,
                        })
                      : '$--'
                  }
                >
                  projected annually
                </Alert>
              </div>
            </div>

            <W2IncomeWarning
              inputTouched={touched.scorpPracticeWIncome}
              w2Income={values.scorpPracticeWIncome}
              isScorpW2Input
            />
          </div>
        )}

        {values.enterScorpPaystubW2 === false && (
          <div className="max-w-2xl mx-auto">
            <Form.Input
              name={getFieldName<typeof values>(
                'estimatedAnnualScorpPracticeW2Income'
              )}
              required
              componentType="currency"
              schema={makeNumberSchema({
                allowedDecimals: 2,
                greaterThanZero: true,
              })}
              placeholder="$"
              label={`${activeQuarterDetails?.taxYear} Annual gross income for W-2 S corporation`}
              description="Enter in the expected W-2 gross income you expect to earn as an employee of your S corp. Do not enter your net profit."
            />
          </div>
        )}
        <div className="max-w-3xl mx-auto">
          <PaystubVsEstimateAccordion content={PaystubAccordionContent.scorp} />
        </div>
        <TaxProfileAccordions
          faqs={[
            faqCopy.onlyWantIncludeBusinessProfit,
            faqCopy.whyNeedEnterPaystubIncome,
            faqCopy.howDoIEstimate,
            faqCopy.whatIsYearToDate,
            faqCopy.whatIsGrossIncome,
          ]}
        />

        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
